import { z } from 'zod';
import { ITEM_ROLES, PRIORITIES, RETRO_COLUMNS, SEVERITIES } from '../enums';
import { uuid } from './common';

// ---------------------------------------------------------------------------------------------
// Approvals (FR-WF-7) and go/no-go (FR-REL-8 Phase 2)
// ---------------------------------------------------------------------------------------------

export const decideApprovalSchema = z.object({ comment: z.string().max(4000).optional() });
export const rejectApprovalSchema = z.object({ comment: z.string().min(1, 'Explain the decision').max(4000) });
export const goNoGoRequestSchema = z.object({ buildId: uuid, comment: z.string().max(4000).optional() });

// ---------------------------------------------------------------------------------------------
// Automation rules (FR-AUTO-1/3): trigger → conditions → actions
// ---------------------------------------------------------------------------------------------

export const AUTOMATION_TRIGGERS = ['item.created', 'item.status_changed', 'item.updated', 'item.assigned', 'comment.created', 'subtasks.all_done', 'build.ready_for_qa'] as const;
export type AutomationTrigger = (typeof AUTOMATION_TRIGGERS)[number];

export const CONDITION_FIELDS = ['type', 'priority', 'severity', 'status', 'statusCategory', 'fromStatus', 'component', 'release', 'sprint', 'assignee', 'reporter', 'storyPoints', 'title', 'changedField'] as const;
export const CONDITION_OPS = ['eq', 'neq', 'in', 'not_in', 'empty', 'not_empty', 'contains', 'gt', 'lt'] as const;

export const conditionSchema = z.object({
  field: z.enum(CONDITION_FIELDS),
  op: z.enum(CONDITION_OPS),
  value: z.union([z.string(), z.number(), z.array(z.string())]).optional(),
});
export type AutomationCondition = z.infer<typeof conditionSchema>;

export const actionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('set_field'), field: z.enum(['priority', 'severity', 'storyPoints', 'dueInDays']), value: z.union([z.string(), z.number()]) }),
  z.object({ type: z.literal('assign'), to: z.enum(['user', 'role', 'reporter', 'round_robin', 'unassign']), userId: uuid.optional(), role: z.enum(ITEM_ROLES).optional(), pool: z.array(uuid).max(50).optional() }),
  z.object({ type: z.literal('set_role'), role: z.enum(ITEM_ROLES), to: z.enum(['user', 'round_robin', 'assignee']), userId: uuid.optional(), pool: z.array(uuid).max(50).optional() }),
  z.object({ type: z.literal('transition'), target: z.enum(['item', 'parent']).default('item'), toStatusKey: z.string().min(1).max(60) }),
  z.object({ type: z.literal('comment'), text: z.string().min(1).max(4000) }),
  z.object({ type: z.literal('notify'), to: z.enum(['users', 'assignee', 'reporter', 'watchers', 'role']), userIds: z.array(uuid).max(50).optional(), role: z.enum(ITEM_ROLES).optional(), message: z.string().max(500).optional() }),
  z.object({ type: z.literal('add_watcher'), userId: uuid }),
  z.object({ type: z.literal('post_channel'), channel: z.enum(['team', 'qa', 'announcements', 'release']), text: z.string().min(1).max(2000) }),
]);
export type AutomationAction = z.infer<typeof actionSchema>;

export const automationRuleSchema = z.object({
  name: z.string().min(1).max(120),
  trigger: z.enum(AUTOMATION_TRIGGERS),
  conditions: z.array(conditionSchema).max(20).default([]),
  actions: z.array(actionSchema).min(1).max(10),
  enabled: z.boolean().default(true),
});
export type AutomationRuleInput = z.infer<typeof automationRuleSchema>;

/** Values for set_field are validated per field when the rule is saved. */
export const SET_FIELD_VALUES = { priority: PRIORITIES, severity: SEVERITIES } as const;

// ---------------------------------------------------------------------------------------------
// Retrospectives (FR-PLN-8)
// ---------------------------------------------------------------------------------------------

export const retroItemSchema = z.object({ column: z.enum(RETRO_COLUMNS), content: z.string().min(1).max(2000) });
export const retroToItemSchema = z.object({ type: z.enum(['task', 'story', 'quick_task']).default('task'), assigneeId: uuid.nullable().optional(), title: z.string().min(1).max(255).optional() });
